import { useState, useRef, useEffect } from 'react';
import MaintenanceCard from './MaintenanceCard';
import MarketValue from './MarketValue';
import HealthScore from './HealthScore';
import ServiceHistory from './ServiceHistory';
import MileageLog from './MileageLog';
import DIYGuide from './DIYGuide';
import StartRepair from './StartRepair';
import ShareVehicleModal from './ShareVehicleModal';
import InternalNotes from './InternalNotes';
import Reminders from './Reminders';
import EditVehicle from './EditVehicle';
import VehicleActionModal from './VehicleActionModal';
import PhotoPickerModal from '../PhotoPickerModal';
import { compressImageFile } from '../../utils/imageUtils';
import { getDismissedItems, hasAcceptedLegal, acceptLegalDocument, saveAnalysis, getAnalysis, addValueSnapshot, getServiceHistory, saveServiceHistory, getAllSavedGuides, getSubscriptionLimits } from '../../utils/storage';
import { getRemainingCredits, hasCredits, CREDIT_COSTS } from '../../utils/credits';
import { isNativeApp } from '../../utils/platform';
import { formatDistance, distanceLabelFull } from '../../utils/units';
import { useAuth } from '../../contexts/AuthContext';
import { useVehiclePermissions } from '../../hooks/useVehiclePermissions';
import { updateMaintenanceRecommendations, updateMarketValuation } from '../../services/partialAnalysis';
import { saveAnalysisToSupabase, updateMaintenanceInSupabase, updateValuationInSupabase, saveValueSnapshotToSupabase, loadVehicleAnalysis, loadVehicleValueHistory } from '../../services/analysisService';
import { parseValueSnapshot } from '../../services/analysisUtils';
import { loadVehicleServiceLogs, mapServiceLogToLocal } from '../../services/serviceLogService';
import { reliableWrite } from '../../services/writeQueue';
import LegalModal, { LEGAL_VERSION } from '../LegalModal';

const REFRESH_COST = 3;

export default function VehicleDetail({
  vehicle,
  analysis,
  serviceHistory,
  garage,
  onOpenChat,
  onOpenChatWithContext,
  onBack,
  onOpenAutoAdvisor,
  onAddServiceEntry,
  onUpdateServiceEntry,
  onRemoveServiceEntry,
  onUpdateVehicle,
  onRemoveVehicle,
  onMoveVehicle,
  onReanalyze,
  onAnalysisUpdated,
  onToast,
  garages,
  reminders = [],
  onAddReminder,
  onUpdateReminder,
  onCompleteReminder,
  onDeleteReminder,
}) {
  const { user } = useAuth();
  const { canLog, canService, canFull, loading: permLoading } = useVehiclePermissions(garage, user?.id, user?.email);
  const [showShare, setShowShare] = useState(false);
  const [showMileageLog, setShowMileageLog] = useState(false);
  const [showAddService, setShowAddService] = useState(false);
  const [showStartRepair, setShowStartRepair] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [diyGuideItem, setDiyGuideItem] = useState(null);
  const [showSafetyDisclaimer, setShowSafetyDisclaimer] = useState(false);
  const [pendingDiyItem, setPendingDiyItem] = useState(null);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);
  const [updatingMaintenance, setUpdatingMaintenance] = useState(false);
  const [updatingValues, setUpdatingValues] = useState(false);
  const [loadingSharedData, setLoadingSharedData] = useState(false);
  const [checkedRemote, setCheckedRemote] = useState(false);
  const photoInputRef = useRef(null);

  // On-demand loading: when analysis is null (e.g. shared vehicle), try fetching from Supabase
  useEffect(() => {
    if (analysis || checkedRemote || !vehicle?.id) return;

    let cancelled = false;
    setLoadingSharedData(true);

    (async () => {
      try {
        const remoteAnalysis = await loadVehicleAnalysis(vehicle.id);
        if (cancelled) return;
        if (remoteAnalysis) {
          // Save analysis to localStorage
          saveAnalysis(vehicle.id, remoteAnalysis);

          // Also load service logs for this vehicle
          const logs = await loadVehicleServiceLogs(vehicle.id);
          if (!cancelled && logs.length > 0) {
            const mapped = logs.map(mapServiceLogToLocal);
            const existing = getServiceHistory();
            const existingIds = new Set(existing.map((e) => e.id));
            const newEntries = mapped.filter((e) => !existingIds.has(e.id));
            if (newEntries.length > 0) {
              saveServiceHistory([...newEntries, ...existing]);
            }
          }

          // Also load value history
          const valueHistory = await loadVehicleValueHistory(vehicle.id);
          if (!cancelled && valueHistory.length > 0) {
            const raw = localStorage.getItem('pm_value_history');
            const allValues = raw ? JSON.parse(raw) : {};
            allValues[vehicle.id] = valueHistory;
            localStorage.setItem('pm_value_history', JSON.stringify(allValues));
          }

          // Trigger parent to reloadAll() so analysis appears in state
          onAnalysisUpdated?.();
        }
      } catch (err) {
        if (import.meta.env.DEV) console.error('[VehicleDetail] On-demand shared data load failed:', err);
      } finally {
        if (!cancelled) {
          setLoadingSharedData(false);
          setCheckedRemote(true);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [vehicle?.id, analysis, checkedRemote]);

  // Guard: vehicle must exist
  if (!vehicle) return null;

  // Calculate days since last analysis
  const analyzedAt = analysis?.analyzedAt;
  const daysSinceAnalysis = analyzedAt
    ? Math.floor((Date.now() - new Date(analyzedAt).getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const isStale = daysSinceAnalysis !== null && daysSinceAnalysis >= 1;

  function handleRefreshClick() {
    setShowRefreshModal(true);
  }

  function handleConfirmRefresh() {
    if (!hasCredits(REFRESH_COST)) {
      onToast?.('Not enough credits. Upgrade your plan for more.');
      setShowRefreshModal(false);
      return;
    }
    setShowRefreshModal(false);
    onReanalyze();
  }

  async function handleUpdateRecommendations() {
    if (!hasCredits(CREDIT_COSTS.maintenanceUpdate)) {
      onToast?.('Not enough credits. Upgrade your plan for more.');
      return;
    }
    setUpdatingMaintenance(true);
    try {
      const result = await updateMaintenanceRecommendations(vehicle, serviceHistory);
      // Merge into existing analysis
      const currentAnalysis = getAnalysis(vehicle.id) || analysis || {};
      const merged = {
        ...currentAnalysis,
        maintenanceItems: result.maintenanceItems,
        healthScore: result.healthScore,
        healthSummary: result.healthSummary,
      };
      saveAnalysis(vehicle.id, merged);
      // Reliable Supabase update (queues for retry on failure)
      if (user?.id) {
        reliableWrite('maintenance', { userId: user.id, vehicleId: vehicle.id, maintenanceItems: result.maintenanceItems, healthScore: result.healthScore, healthSummary: result.healthSummary }, () =>
          updateMaintenanceInSupabase(user.id, vehicle.id, result.maintenanceItems, result.healthScore, result.healthSummary)
        );
      }
      onAnalysisUpdated?.();
      onToast?.('Recommendations updated!');
    } catch (err) {
      onToast?.('Update failed. Please try again.');
      if (import.meta.env.DEV) console.error('[VehicleDetail] Maintenance update failed:', err);
    } finally {
      setUpdatingMaintenance(false);
    }
  }

  async function handleUpdateValues() {
    if (!hasCredits(CREDIT_COSTS.valuationUpdate)) {
      onToast?.('Not enough credits. Upgrade your plan for more.');
      return;
    }
    setUpdatingValues(true);
    try {
      const result = await updateMarketValuation(vehicle, serviceHistory);
      // Merge into existing analysis
      const currentAnalysis = getAnalysis(vehicle.id) || analysis || {};
      const merged = {
        ...currentAnalysis,
        marketValue: result.marketValue,
      };
      saveAnalysis(vehicle.id, merged);
      // Value snapshot
      const snapshot = parseValueSnapshot(merged);
      if (snapshot.marketValueLow || snapshot.marketValueHigh || snapshot.healthScore) {
        addValueSnapshot(vehicle.id, snapshot);
        if (user?.id) {
          reliableWrite('valueSnapshot', { userId: user.id, vehicleId: vehicle.id, snapshot }, () =>
            saveValueSnapshotToSupabase(user.id, vehicle.id, snapshot)
          );
        }
      }
      // Reliable Supabase update (queues for retry on failure)
      if (user?.id) {
        reliableWrite('valuation', { userId: user.id, vehicleId: vehicle.id, marketValue: result.marketValue }, () =>
          updateValuationInSupabase(user.id, vehicle.id, result.marketValue)
        );
      }
      onAnalysisUpdated?.();
      onToast?.('Market values updated!');
    } catch (err) {
      onToast?.('Update failed. Please try again.');
      if (import.meta.env.DEV) console.error('[VehicleDetail] Valuation update failed:', err);
    } finally {
      setUpdatingValues(false);
    }
  }

  function handleOpenDIYGuide(item) {
    // Check if safety disclaimer has been accepted
    if (!hasAcceptedLegal('safety', LEGAL_VERSION)) {
      setPendingDiyItem(item);
      setShowSafetyDisclaimer(true);
    } else {
      setDiyGuideItem(item);
    }
  }

  function handleAcceptSafety() {
    acceptLegalDocument('safety', LEGAL_VERSION);
    setShowSafetyDisclaimer(false);
    if (pendingDiyItem) {
      setDiyGuideItem(pendingDiyItem);
      setPendingDiyItem(null);
    }
  }

  function handleAskPocketMechanic(step) {
    const service = diyGuideItem?.service || 'this maintenance service';
    const message = `I'm currently working on my ${vehicle.year} ${vehicle.make} ${vehicle.model}. I'm doing: ${service}. I'm on step ${step.number}: "${step.title}" — ${step.description}. Can you give me as much detail as possible for how to complete this step? Include tips, tricks, best practices, and anything specific to my vehicle.`;
    if (onOpenChatWithContext) {
      onOpenChatWithContext(message);
    }
  }

  async function handlePhotoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImageFile(file, 800, 0.8);
      onUpdateVehicle({ ...vehicle, photo: compressed });
    } catch {
      onToast?.('Failed to upload photo');
    }
    e.target.value = '';
  }

  // ─── Vehicle action handlers ───

  function handleSold(saleData) {
    // Find or create "Sold Vehicles" garage
    const allGarages = garages || [];
    let soldGarage = allGarages.find((g) => g.name === 'Sold Vehicles');
    if (!soldGarage) {
      // Will be created via onMoveVehicle's parent (App.jsx addGarage)
      // For now, we signal the parent to handle this
    }
    // Update vehicle with sale data and status
    const updatedVehicle = {
      ...vehicle,
      status: 'sold',
      saleData,
      soldAt: new Date().toISOString(),
    };
    onUpdateVehicle(updatedVehicle);
    // Move to Sold Vehicles garage (parent will create if needed)
    if (onMoveVehicle) {
      onMoveVehicle(vehicle.id, '__sold__');
    }
    setShowActionModal(false);
    onToast?.('Vehicle marked as sold');
    onBack();
  }

  function handleArchive() {
    const updatedVehicle = { ...vehicle, status: 'archived', archivedAt: new Date().toISOString() };
    onUpdateVehicle(updatedVehicle);
    if (onMoveVehicle) {
      onMoveVehicle(vehicle.id, '__archived__');
    }
    setShowActionModal(false);
    onToast?.('Vehicle archived');
    onBack();
  }

  function handleDeletePermanently() {
    setShowActionModal(false);
    if (onRemoveVehicle) {
      onRemoveVehicle(vehicle.id);
    }
    onToast?.('Vehicle permanently deleted');
    onBack();
  }

  // ─── Edit mode ───
  if (showEdit) {
    return (
      <EditVehicle
        vehicle={vehicle}
        onSave={(updatedVehicle) => {
          onUpdateVehicle(updatedVehicle);
          setShowEdit(false);
          onToast?.('Vehicle updated');
        }}
        onBack={() => setShowEdit(false)}
        onDelete={() => { setShowEdit(false); setShowActionModal(true); }}
        onToast={onToast}
        canFull={canFull}
      />
    );
  }

  const { year, make, model, mileage } = vehicle;
  const label = `${year} ${make} ${model}`;
  const isShared = vehicle.collaborators && vehicle.collaborators.length > 0;

  if (!analysis) {
    // Show loading spinner while fetching shared vehicle data from Supabase
    if (loadingSharedData) {
      return (
        <div className="screen" style={{ justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ position: 'absolute', top: 24, left: 20 }}>
            <button
              onClick={onBack}
              style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'var(--bg-elevated)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: 'none', cursor: 'pointer',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              border: '2.5px solid var(--border)', borderTopColor: 'var(--accent)',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading vehicle data...</p>
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      );
    }

    return (
      <div className="screen" style={{ justifyContent: 'center', alignItems: 'center' }}>
        {/* Back button */}
        <div style={{ position: 'absolute', top: 24, left: 20 }}>
          <button
            onClick={onBack}
            style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'var(--bg-elevated)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: 'none', cursor: 'pointer',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        </div>

        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'var(--accent-subtle)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
            </svg>
          </div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: 8 }}>{label}</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: 24, lineHeight: 1.5 }}>
            {canService
              ? 'This vehicle hasn\'t been analyzed yet. Run an analysis to get maintenance recommendations, market value, and health score.'
              : 'This vehicle hasn\'t been analyzed yet. The owner needs to run an analysis first.'}
          </p>
          {canService && (
            <button className="btn btn-primary" onClick={onReanalyze}>
              Analyze Vehicle
            </button>
          )}
          {!canService && (
            <button
              className="btn btn-secondary"
              onClick={() => setCheckedRemote(false)}
              style={{ marginTop: 8 }}
            >
              Retry Loading
            </button>
          )}
        </div>
      </div>
    );
  }

  // Filter out dismissed AND completed maintenance items
  const dismissedItems = getDismissedItems(vehicle.id);
  const dismissedServices = dismissedItems.map((d) => d.service);
  const completedServices = (serviceHistory || []).map((e) => e.service);
  const visibleMaintenance = (analysis.maintenanceItems || []).filter(
    (item) => !dismissedServices.includes(item.service) && !completedServices.includes(item.service)
  );

  if (showMileageLog) {
    return (
      <MileageLog
        vehicle={vehicle}
        onBack={() => setShowMileageLog(false)}
        onUpdateVehicle={onUpdateVehicle}
        onToast={onToast}
      />
    );
  }

  if (showStartRepair && !diyGuideItem) {
    return (
      <StartRepair
        onBack={() => setShowStartRepair(false)}
        onStart={(item) => {
          setShowStartRepair(false);
          handleOpenDIYGuide(item);
        }}
      />
    );
  }

  if (diyGuideItem) {
    return (
      <DIYGuide
        vehicle={vehicle}
        item={diyGuideItem}
        onBack={() => setDiyGuideItem(null)}
        onMarkComplete={(entry) => {
          onAddServiceEntry(entry);
          setDiyGuideItem(null);
          onToast?.(`"${diyGuideItem.service}" marked complete!`);
        }}
        onAskPocketMechanic={onOpenChatWithContext ? handleAskPocketMechanic : undefined}
      />
    );
  }

  return (
    <div className="screen" style={{ paddingBottom: 100 }}>
      {/* Vehicle banner photo */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handlePhotoUpload}
      />
      <button
        onClick={() => setShowPhotoPicker(true)}
        style={{
          width: 'calc(100% + 40px)',
          marginLeft: -20,
          marginTop: -20,
          marginBottom: 16,
          height: vehicle.photo ? 200 : 80,
          background: vehicle.photo
            ? `url(${vehicle.photo}) center/cover no-repeat`
            : 'var(--bg-elevated)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          cursor: 'pointer',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {!vehicle.photo && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            Add Vehicle Photo
          </div>
        )}
        {vehicle.photo && (
          <div style={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </div>
        )}
      </button>

      {/* Last analyzed + refresh */}
      {analyzedAt && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
          padding: '0 2px',
        }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Last analyzed: {daysSinceAnalysis === 0 ? 'today' : daysSinceAnalysis === 1 ? '1 day ago' : `${daysSinceAnalysis} days ago`}
          </span>
          {isStale && canService && (
            <button
              onClick={handleRefreshClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                padding: '4px 10px',
                borderRadius: 100,
                border: '1px solid var(--accent)',
                background: 'transparent',
                color: 'var(--accent)',
                fontSize: '0.6875rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
              </svg>
              Refresh Analysis
            </button>
          )}
        </div>
      )}

      {/* Vehicle header with back button and share */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button
          onClick={onBack}
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: 'var(--bg-elevated)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h1 className="screen-title" style={{ fontSize: '1.25rem' }}>{label}</h1>
            {isShared && (
              <span
                style={{
                  padding: '2px 7px',
                  borderRadius: 100,
                  background: 'rgba(59, 130, 246, 0.12)',
                  color: '#3b82f6',
                  fontSize: '0.625rem',
                  fontWeight: 600,
                }}
              >
                Shared
              </span>
            )}
          </div>
          <p style={{ color: vehicle.tmu ? 'var(--warning)' : 'var(--text-muted)', fontSize: '0.875rem' }}>
            {mileage ? formatDistance(mileage, vehicle.distance_units) : 'No mileage recorded'}
            {vehicle.tmu && ' (TMU)'}
          </p>
          {vehicle.tmu && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.6875rem', marginTop: 2 }}>
              {`*Total Mileage Unknown \u2014 may not reflect actual ${distanceLabelFull(vehicle.distance_units)} driven`}
            </p>
          )}
        </div>
        {/* Edit button — requires SERVICE_ACCESS */}
        {canService && (
          <button
            onClick={() => setShowEdit(true)}
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: 'var(--bg-elevated)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        )}
        {/* Share button — requires FULL_ACCESS + Team plan */}
        {canFull && getSubscriptionLimits().canShare && (
          <button
            onClick={() => setShowShare(true)}
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: 'var(--bg-elevated)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
          </button>
        )}
      </div>

      {/* Collaborators list */}
      {isShared && (
        <div style={{
          marginBottom: 16,
          padding: '10px 14px',
          background: 'var(--bg-elevated)',
          borderRadius: 'var(--radius-sm)',
          fontSize: '0.75rem',
          color: 'var(--text-secondary)',
        }}>
          Shared with: {vehicle.collaborators.map((c) => c.email).join(', ')}
        </div>
      )}

      {/* Action Buttons — gated by permission */}
      {(canLog || canService) && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {canLog && (
            <ActionButton
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20V10"/><path d="M18 20V4"/><path d="M6 20v-4"/></svg>}
              label={"Log\nMiles"}
              onClick={() => setShowMileageLog(true)}
            />
          )}
          {canLog && (
            <ActionButton
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
              label={"Log\nService"}
              onClick={() => setShowAddService(true)}
            />
          )}
          {canService && (
            <ActionButton
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>}
              label={"Start\nRepair"}
              onClick={() => setShowStartRepair(true)}
            />
          )}
          {canService && (
            <ActionButton
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>}
              label={"Pocket\nMechanic"}
              onClick={onOpenChat}
            />
          )}
        </div>
      )}

      {/* 0. Your Reminders */}
      <Reminders
        vehicleId={vehicle.id}
        reminders={reminders}
        onAdd={onAddReminder}
        onUpdate={onUpdateReminder}
        onComplete={onCompleteReminder}
        onDelete={onDeleteReminder}
        canLog={canLog}
        canService={canService}
        onToast={onToast}
      />

      {/* 0.5. Saved Repairs (in-progress DIY guides) */}
      {(() => {
        const allGuides = getAllSavedGuides(vehicle.id);
        const savedRepairs = Object.values(allGuides || {}).filter((g) => {
          if (!g.guide?.steps) return false;
          const done = (g.completedSteps || []).length;
          const total = g.guide.steps.length;
          // Only show incomplete guides not already in service history
          return done < total && !completedServices.includes(g.service);
        });
        if (savedRepairs.length === 0) return null;
        return (
          <div style={{ marginBottom: 20 }}>
            <h2 style={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              margin: 0,
              marginBottom: 12,
            }}>
              Saved Repairs
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {savedRepairs.map((g) => {
                const done = (g.completedSteps || []).length;
                const total = g.guide.steps.length;
                const pct = Math.round((done / total) * 100);
                return (
                  <button
                    key={g.service}
                    onClick={() => handleOpenDIYGuide({
                      service: g.service,
                      difficulty: 'Unknown',
                      description: `Saved repair: ${g.service}`,
                      costDealer: 'Unknown',
                      costShop: 'Unknown',
                      costDIY: 'Unknown',
                      source: 'saved_repair',
                    })}
                    className="card"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '14px 16px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      width: '100%',
                    }}
                  >
                    {/* Circular progress indicator */}
                    <div style={{
                      width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                      background: `conic-gradient(var(--accent) ${pct * 3.6}deg, var(--bg-elevated) 0deg)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <div style={{
                        width: 30, height: 30, borderRadius: '50%',
                        background: 'var(--bg-card)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.625rem', fontWeight: 700, color: 'var(--accent)',
                      }}>
                        {pct}%
                      </div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {g.service}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                        {done} of {total} steps · {g.lastAccessedAt ? new Date(g.lastAccessedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                      </div>
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* 1. Recommended Maintenance */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2
            style={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              margin: 0,
            }}
          >
            Recommended Maintenance
          </h2>
          {canService && (
            <button
              onClick={handleUpdateRecommendations}
              disabled={updatingMaintenance}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 10px',
                borderRadius: 100,
                border: '1px solid var(--accent)',
                background: 'transparent',
                color: 'var(--accent)',
                fontSize: '0.6875rem',
                fontWeight: 600,
                cursor: updatingMaintenance ? 'default' : 'pointer',
                opacity: updatingMaintenance ? 0.6 : 1,
              }}
            >
              {updatingMaintenance ? (
                <div style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid var(--accent)', borderTopColor: 'transparent', animation: 'spin 1s linear infinite' }} />
              ) : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10" />
                  <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
                </svg>
              )}
              {updatingMaintenance ? 'Updating...' : 'Update'}
            </button>
          )}
        </div>
        {visibleMaintenance.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            All maintenance items are up to date!
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {visibleMaintenance.map((item, i) => (
              <MaintenanceCard
                key={i}
                item={item}
                vehicleId={vehicle.id}
                skillLevel={vehicle.skillLevel}
                onMarkComplete={onAddServiceEntry}
                onToast={onToast}
                onShowDIYGuide={(item) => handleOpenDIYGuide(item)}
                canLog={canLog}
              />
            ))}
          </div>
        )}
      </div>

      {/* 2. Market Value */}
      <MarketValue data={analysis.marketValue} analyzedAt={analysis.analyzedAt} onRefresh={onReanalyze} onUpdateValues={canService ? handleUpdateValues : null} updatingValues={updatingValues} vehicleId={vehicle.id} serviceLogCount={(serviceHistory || []).length} />

      {/* 3. Health Score */}
      <div style={{ marginTop: 12 }}>
        <HealthScore score={analysis.healthScore} summary={analysis.healthSummary} vehicleId={vehicle.id} />
      </div>

      {/* 4. Service History */}
      <ServiceHistory
        entries={serviceHistory}
        vehicle={vehicle}
        onAddEntry={onAddServiceEntry}
        onUpdateEntry={onUpdateServiceEntry}
        onRemoveEntry={onRemoveServiceEntry}
        onToast={onToast}
        forceShowAdd={showAddService}
        onCancelAdd={() => setShowAddService(false)}
        canLog={canLog}
      />

      {/* 5. Internal Notes (Team garages only) */}
      <InternalNotes
        vehicle={vehicle}
        garage={garage}
        onUpdateVehicle={onUpdateVehicle}
        onToast={onToast}
        canLog={canLog}
      />

      {/* Share modal */}
      {showShare && (
        <ShareVehicleModal
          vehicle={vehicle}
          onUpdate={onUpdateVehicle}
          onClose={() => setShowShare(false)}
          onToast={onToast}
        />
      )}

      {/* Safety disclaimer modal — shown before first DIY guide access */}
      {showSafetyDisclaimer && (
        <LegalModal
          type="safety"
          onClose={() => { setShowSafetyDisclaimer(false); setPendingDiyItem(null); }}
          onAccept={handleAcceptSafety}
        />
      )}

      {/* Vehicle action modal (Sold / Archive / Delete) */}
      {showActionModal && (
        <VehicleActionModal
          vehicle={vehicle}
          serviceHistory={serviceHistory}
          onSold={handleSold}
          onArchive={handleArchive}
          onDeletePermanently={handleDeletePermanently}
          onClose={() => setShowActionModal(false)}
        />
      )}

      {/* Refresh Analysis confirmation modal */}
      {showRefreshModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 200, padding: 20,
          }}
          onClick={() => setShowRefreshModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-card)',
              borderRadius: 'var(--radius-md)',
              padding: 24, width: '100%', maxWidth: 340,
              border: '1px solid var(--border)', textAlign: 'center',
            }}
          >
            {/* Refresh icon */}
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: 'var(--accent-subtle)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
              </svg>
            </div>

            <h3 style={{ fontSize: '1.0625rem', fontWeight: 700, marginBottom: 8 }}>
              Refresh Analysis
            </h3>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 16 }}>
              This will update market value and maintenance schedule with current data.
            </p>

            {/* Credit info */}
            <div style={{
              background: 'var(--bg-elevated)',
              borderRadius: 'var(--radius-sm)',
              padding: 14,
              marginBottom: 20,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>Cost</span>
                <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--accent)' }}>{REFRESH_COST} credits</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>Your balance</span>
                <span style={{
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  color: hasCredits(REFRESH_COST) ? 'var(--text-primary)' : 'var(--danger)',
                }}>
                  {getRemainingCredits()} credits
                </span>
              </div>
              {!hasCredits(REFRESH_COST) && (
                <p style={{ fontSize: '0.75rem', color: 'var(--danger)', marginTop: 8 }}>
                  {'Not enough credits. Upgrade your plan for more.'}
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowRefreshModal(false)}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                disabled={!hasCredits(REFRESH_COST)}
                onClick={handleConfirmRefresh}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  background: hasCredits(REFRESH_COST) ? 'var(--accent)' : 'var(--bg-elevated)',
                  color: hasCredits(REFRESH_COST) ? 'white' : 'var(--text-muted)',
                  cursor: hasCredits(REFRESH_COST) ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s',
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <PhotoPickerModal
        show={showPhotoPicker}
        onClose={() => setShowPhotoPicker(false)}
        onSelect={(source) => {
          setShowPhotoPicker(false);
          photoInputRef.current.removeAttribute('capture');
          photoInputRef.current.click();
        }}
      />
    </div>
  );
}

/* ─── Action Button ─── */

function ActionButton({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        padding: '12px 4px',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-card)',
        color: 'var(--text-primary)',
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
    >
      <div style={{ color: 'var(--accent)' }}>{icon}</div>
      <span style={{ fontSize: '0.6875rem', fontWeight: 600, textAlign: 'center', whiteSpace: 'pre-line', lineHeight: 1.3 }}>{label}</span>
    </button>
  );
}
